"use client";

import { useState } from "react";
import Link from "next/link";
import { BookingModal } from "@/components/BookingModal";
import { ContactFooter } from "@/components/ContactFooter";
import { Calendar, Clock, ArrowLeft, ArrowRight } from "lucide-react";
import type { getBlogBySlug } from "@/lib/blog";

type Blog = NonNullable<ReturnType<typeof getBlogBySlug>>;

export default function BlogPostPageClient({
  blog,
  relatedBlogs,
}: {
  blog: Blog;
  relatedBlogs: Blog[];
}) {
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      <BookingModal open={showModal} onOpenChange={setShowModal} />

      <main className="min-h-screen">
        {/* Hero */}
        <section className="bg-gradient-to-b from-primary/10 to-background pt-32 pb-12 px-4">
          <div className="max-w-3xl mx-auto">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
              <Link href="/" className="hover:text-primary transition-colors">Home</Link>
              <span>/</span>
              <Link href="/news-and-articles" className="hover:text-primary transition-colors">News & Articles</Link>
              <span>/</span>
              <span className="text-foreground font-medium line-clamp-1">{blog.title}</span>
            </nav>

            {/* Category */}
            <span className="inline-block text-xs font-semibold text-primary bg-primary/10 px-3 py-1 rounded-full mb-4">
              {blog.category}
            </span>

            {/* Title */}
            <h1 className="text-3xl md:text-4xl font-bold mb-6 leading-tight">
              {blog.title}
            </h1>

            {/* Meta */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-6">
              <span className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                {blog.date}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="h-4 w-4" />
                {blog.readTime}
              </span>
              <span className="flex items-center gap-1">
                By <span className="text-primary font-medium">SkinSio Clinics, Vadodara</span>
              </span>
            </div>

            {/* Excerpt */}
            <p className="text-lg text-muted-foreground leading-relaxed border-l-4 border-primary pl-4">
              {blog.excerpt}
            </p>
          </div>
        </section>

        {/* Featured Image */}
        <section className="max-w-3xl mx-auto px-4 -mt-4 mb-4">
          <img
            src={blog.image}
            alt={blog.title}
            className="w-full h-auto rounded-2xl shadow-md object-contain"
            width={1200}
            height={630}
          />
        </section>

        {/* Article Content */}
        <section className="max-w-3xl mx-auto px-4 py-12">
          <article
            className="prose prose-lg max-w-none
              prose-headings:font-bold prose-headings:text-foreground
              prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
              prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
              prose-p:text-muted-foreground prose-p:leading-relaxed prose-p:mb-4
              prose-li:text-muted-foreground
              prose-a:text-primary prose-a:no-underline hover:prose-a:underline
              prose-strong:text-foreground
              prose-table:text-sm
              prose-th:bg-primary/10 prose-th:text-foreground
              prose-td:text-muted-foreground"
            dangerouslySetInnerHTML={{ __html: blog.content }}
          />
        </section>

        {/* Internal backlinks section */}
        <section className="max-w-3xl mx-auto px-4 pb-12">
          <div className="bg-primary/5 rounded-2xl p-6">
            <h3 className="font-bold text-lg mb-4">Related Treatments at SkinSio Clinics, Vadodara</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {blog.relatedTreatments.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="flex items-center gap-2 text-sm text-primary font-medium hover:underline"
                >
                  <ArrowRight className="h-3 w-3 flex-shrink-0" />
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-primary/5 py-12 px-4 text-center">
          <div className="max-w-xl mx-auto">
            <h2 className="text-2xl font-bold mb-3">
              Interested in {blog.topic} in Vadodara?
            </h2>
            <p className="text-muted-foreground mb-6">
              Book a consultation with our Experts at SkinSio Clinics, Vadodara.
              All procedures use sterile instruments and medical-grade machines & pigments.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => setShowModal(true)}
                className="inline-block bg-primary text-white px-8 py-3 rounded-full font-semibold hover:opacity-90 transition-opacity"
              >
                Book Consultation
              </button>
              <a
                href="tel:+919322022392"
                className="inline-block border border-primary text-primary px-8 py-3 rounded-full font-semibold hover:bg-primary/5 transition-colors"
              >
                Call +91 93220 22392
              </a>
            </div>
          </div>
        </section>

        {/* Related Articles */}
        {relatedBlogs.length > 0 && (
          <section className="max-w-5xl mx-auto px-4 py-12">
            <h2 className="text-2xl font-bold mb-6">More Articles</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedBlogs.map((related) => (
                <Link
                  key={related.slug}
                  href={`/news-and-articles/${related.slug}`}
                  className="group border rounded-2xl bg-card p-5 hover:shadow-md hover:border-primary/40 transition-all duration-200"
                >
                  <span className="text-xs font-semibold text-primary bg-primary/10 px-2 py-1 rounded-full">
                    {related.category}
                  </span>
                  <h3 className="font-bold mt-3 mb-2 group-hover:text-primary transition-colors line-clamp-2">
                    {related.title}
                  </h3>
                  <span className="text-sm text-primary font-medium flex items-center gap-1">
                    Read more <ArrowRight className="h-3 w-3" />
                  </span>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Back to all articles */}
        <div className="max-w-3xl mx-auto px-4 pb-8">
          <Link
            href="/news-and-articles"
            className="inline-flex items-center gap-2 text-sm text-primary font-medium hover:underline"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to all articles
          </Link>
        </div>

        <ContactFooter />
      </main>
    </>
  );
}
